import { Fragment, ReactNode, useMemo } from 'react';
import AuthenticatedDownloadLink from '../AuthenticatedDownloadLink';
import {
  isGeneratedDocumentDownloadUrl,
  resolveApiHref,
} from '../../lib/api';
import CodeBlock from './CodeBlock';

/**
 * Dependency-free markdown for operator chat: fences, tables, lists, headings,
 * bold/italic, links, inline code. Untrusted model output is never raw HTML.
 *
 * Also soft-normalizes common model quirks (|| as table row breaks, headings
 * jammed onto the previous sentence) without touching fenced code.
 */

function renderInline(text: string, keyBase: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  const pattern =
    /(`[^`]+`)|(\*\*[^*]+\*\*)|(__[^_]+__)|(\*[^*]+\*)|(_[^_]+_)|(\[[^\]]+\]\([^)]+\))/g;
  let last = 0;
  let match: RegExpExecArray | null;
  let i = 0;
  while ((match = pattern.exec(text)) !== null) {
    if (match.index > last) nodes.push(text.slice(last, match.index));
    const token = match[0];
    const key = `${keyBase}-${i++}`;
    if (token.startsWith('`')) {
      nodes.push(
        <code
          key={key}
          className="rounded bg-zt-elevated px-1 py-0.5 font-mono text-[0.85em] text-zt-accent"
        >
          {token.slice(1, -1)}
        </code>
      );
    } else if (token.startsWith('**') || token.startsWith('__')) {
      nodes.push(
        <strong key={key} className="font-semibold text-white">
          {token.slice(2, -2)}
        </strong>
      );
    } else if (
      (token.startsWith('*') && token.endsWith('*') && !token.startsWith('**')) ||
      (token.startsWith('_') && token.endsWith('_') && !token.startsWith('__'))
    ) {
      nodes.push(
        <em key={key} className="italic text-slate-200">
          {token.slice(1, -1)}
        </em>
      );
    } else {
      const m = /\[([^\]]+)\]\(([^)]+)\)/.exec(token);
      if (m) {
        const href = m[2].trim();
        const label = m[1];
        const linkClass =
          'text-zt-primary underline underline-offset-2 hover:text-zt-accent';
        if (isGeneratedDocumentDownloadUrl(href) || href.startsWith('/api/')) {
          nodes.push(
            <AuthenticatedDownloadLink
              key={key}
              href={resolveApiHref(href)}
              forceAuthenticated
              className={linkClass}
            >
              {label}
            </AuthenticatedDownloadLink>
          );
        } else {
          nodes.push(
            <a
              key={key}
              href={href}
              target="_blank"
              rel="noreferrer noopener"
              className={linkClass}
            >
              {label}
            </a>
          );
        }
      }
    }
    last = match.index + token.length;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
}

/** True when || is being used as a table row break (pipe-heavy, not `a || b` prose). */
function shouldFixDoublePipeRows(text: string): boolean {
  if (!/\|\|/.test(text)) return false;
  return (text.match(/\|/g) || []).length >= 4;
}

/**
 * Soft-normalize model quirks outside fenced code. Safe to run repeatedly.
 * Does not strip legitimate newlines; only inserts structure when missing.
 */
export function normalizeMarkdownProse(text: string): string {
  if (!text) return text;
  let s = text.replace(/\r\n/g, '\n');

  if (shouldFixDoublePipeRows(s)) {
    // Model often uses || as a row separator instead of a newline.
    s = s.replace(/\|\|/g, '|\n|');
  }

  // Header row glued to separator: "| Value |------|-------|" → split before dashes.
  // Require a non-dash cell (letter/digit) so real "|---|" separator rows stay intact.
  s = s.replace(/(\|[^|\n]*[A-Za-z0-9][^|\n]*)\|(?=\s*:?-+:?\s*\|)/g, '$1|\n|');

  // Standalone --- HR jammed between prose. Exclude '-' so |---| dash runs stay intact.
  s = s.replace(/([^\n|\s-])(-{3,})([^\n|\s-])/g, '$1\n\n$2\n\n$3');

  // ATX headings jammed onto previous text (don't split ## into # + #).
  s = s.replace(/([^\n#])(#{1,3}\s+\S)/g, '$1\n\n$2');

  // Heading title immediately followed by a table: "## Title| Col |"
  s = s.replace(/(#{1,3}\s+[^\n|]+?)(\|(?=[^\n]*\|))/g, '$1\n\n$2');

  // Table starting immediately after sentence punctuation / colon.
  s = s.replace(/([.:!?])(\|(?=[^|\n]*\|))/g, '$1\n\n$2');

  // Collapse accidental 3+ blank lines from stacked fixes.
  s = s.replace(/\n{3,}/g, '\n\n');
  return s;
}

/** Apply prose normalization only outside ``` fences. */
export function normalizeMarkdownContent(content: string): string {
  const parts: string[] = [];
  const re = /(```[\w]*\n?[\s\S]*?```)/g;
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(content)) !== null) {
    if (m.index > last) parts.push(normalizeMarkdownProse(content.slice(last, m.index)));
    parts.push(m[0]);
    last = m.index + m[0].length;
  }
  if (last < content.length) parts.push(normalizeMarkdownProse(content.slice(last)));
  return parts.join('');
}

function isSepLine(line: string): boolean {
  const sep = line.replace(/\s/g, '');
  return /^\|?(:?-+:?\|)+(:?-+:?)?$/.test(sep) || /^(:?-+:?\|)+(:?-+:?)$/.test(sep);
}

function isTableBlock(block: string): boolean {
  const lines = block.split('\n').filter((l) => l.trim());
  if (lines.length < 2) return false;
  if (!lines.every((l) => l.includes('|'))) return false;
  return isSepLine(lines[1]);
}

function isHrLine(line: string): boolean {
  return /^(-{3,}|\*{3,}|_{3,})$/.test(line.trim());
}

function isHeadingLine(line: string): RegExpExecArray | null {
  return /^(#{1,3})\s+(.*)$/.exec(line.trim());
}

function renderTable(block: string, keyBase: string): ReactNode {
  const lines = block.split('\n').filter((l) => l.trim());
  const splitRow = (line: string) =>
    line
      .trim()
      .replace(/^\|/, '')
      .replace(/\|$/, '')
      .split('|')
      .map((c) => c.trim());
  const header = splitRow(lines[0]);
  const rows = lines.slice(2).map(splitRow);
  return (
    <div key={keyBase} className="my-3 overflow-x-auto rounded-md border border-zt-border/80">
      <table className="min-w-full border-collapse text-left text-xs">
        <thead>
          <tr className="border-b border-zt-border bg-zt-elevated/60">
            {header.map((h, i) => (
              <th
                key={`${keyBase}-h-${i}`}
                className="border-r border-zt-border/50 px-3 py-2 font-semibold text-slate-100 last:border-r-0"
              >
                {renderInline(h, `${keyBase}-h-${i}`)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, ri) => (
            <tr key={`${keyBase}-r-${ri}`} className="border-b border-zt-border/40 last:border-b-0">
              {header.map((_, ci) => (
                <td
                  key={`${keyBase}-r-${ri}-c-${ci}`}
                  className="border-r border-zt-border/30 px-3 py-2 text-slate-300 last:border-r-0"
                >
                  {renderInline(row[ci] ?? '', `${keyBase}-r-${ri}-c-${ci}`)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function renderHeading(level: number, text: string, keyBase: string): ReactNode {
  const cls =
    level === 1
      ? 'mt-4 mb-2 text-base font-semibold'
      : level === 2
        ? 'mt-3.5 mb-1.5 text-sm font-semibold'
        : 'mt-3 mb-1 text-sm font-medium';
  return (
    <p key={keyBase} className={`${cls} text-white first:mt-0`}>
      {renderInline(text, keyBase)}
    </p>
  );
}

function renderParagraph(text: string, keyBase: string): ReactNode {
  if (!text.trim()) return null;
  return (
    <p key={keyBase} className="whitespace-pre-wrap leading-relaxed">
      {renderInline(text, keyBase)}
    </p>
  );
}

/**
 * Render a prose block that may mix paragraphs, headings, and HRs on single newlines.
 * Tables / lists that fill the whole block are handled first.
 */
function renderTextBlock(block: string, keyBase: string): ReactNode {
  if (isTableBlock(block)) return renderTable(block, keyBase);

  const lines = block.split('\n');
  const nonEmpty = lines.filter((l) => l.trim());
  const isBulleted =
    nonEmpty.length > 0 && nonEmpty.every((l) => /^\s*[-*]\s+/.test(l));
  const isNumbered =
    nonEmpty.length > 0 && nonEmpty.every((l) => /^\s*\d+\.\s+/.test(l));

  if (isBulleted) {
    return (
      <ul key={keyBase} className="my-2 list-disc space-y-1 pl-5">
        {nonEmpty.map((l, idx) => (
          <li key={`${keyBase}-li-${idx}`}>
            {renderInline(l.replace(/^\s*[-*]\s+/, ''), `${keyBase}-${idx}`)}
          </li>
        ))}
      </ul>
    );
  }
  if (isNumbered) {
    return (
      <ol key={keyBase} className="my-2 list-decimal space-y-1 pl-5">
        {nonEmpty.map((l, idx) => (
          <li key={`${keyBase}-li-${idx}`}>
            {renderInline(l.replace(/^\s*\d+\.\s+/, ''), `${keyBase}-${idx}`)}
          </li>
        ))}
      </ol>
    );
  }

  const headingOnly = isHeadingLine(block);
  if (headingOnly && !block.includes('\n')) {
    return renderHeading(headingOnly[1].length, headingOnly[2], keyBase);
  }

  const hasStructure = lines.some((l) => isHeadingLine(l) || isHrLine(l) || l.includes('|'));

  // Whole-block table may have been missed if separator detection failed earlier;
  // try contiguous pipe-line runs.
  if (hasStructure) {
    const nodes: ReactNode[] = [];
    let paraBuf: string[] = [];
    let tableBuf: string[] = [];
    let idx = 0;

    const flushPara = () => {
      if (!paraBuf.length) return;
      const text = paraBuf.join('\n');
      paraBuf = [];
      const node = renderParagraph(text, `${keyBase}-p-${idx++}`);
      if (node) nodes.push(node);
    };

    const flushTable = () => {
      if (!tableBuf.length) return;
      const text = tableBuf.join('\n');
      tableBuf = [];
      if (isTableBlock(text)) {
        nodes.push(renderTable(text, `${keyBase}-tbl-${idx++}`));
      } else {
        const node = renderParagraph(text, `${keyBase}-p-${idx++}`);
        if (node) nodes.push(node);
      }
    };

    for (const line of lines) {
      const heading = isHeadingLine(line);
      if (heading) {
        flushTable();
        flushPara();
        nodes.push(renderHeading(heading[1].length, heading[2], `${keyBase}-h-${idx++}`));
        continue;
      }
      if (isHrLine(line)) {
        flushTable();
        flushPara();
        nodes.push(
          <hr key={`${keyBase}-hr-${idx++}`} className="my-3 border-0 border-t border-zt-border" />
        );
        continue;
      }
      if (line.includes('|')) {
        flushPara();
        tableBuf.push(line);
        continue;
      }
      flushTable();
      paraBuf.push(line);
    }
    flushTable();
    flushPara();

    if (nodes.length === 1) return nodes[0];
    return (
      <div key={keyBase} className="space-y-2">
        {nodes}
      </div>
    );
  }

  return renderParagraph(block, keyBase);
}

function Markdown({ content }: { content: string }) {
  const parts = useMemo(() => {
    const normalized = normalizeMarkdownContent(content);
    const segments = normalized.split(/```(\w*)\n?([\s\S]*?)```/g);
    const out: ReactNode[] = [];
    for (let i = 0; i < segments.length; ) {
      const text = segments[i];
      if (text) {
        const blocks = text.split(/\n{2,}/).filter((b) => b.trim().length > 0);
        blocks.forEach((b, bi) => out.push(renderTextBlock(b, `t-${i}-${bi}`)));
      }
      if (i + 2 < segments.length) {
        const lang = segments[i + 1];
        const code = segments[i + 2];
        out.push(<CodeBlock key={`code-${i}`} code={code.replace(/\n$/, '')} language={lang} />);
        i += 3;
      } else {
        i += 1;
      }
    }
    return out;
  }, [content]);

  return (
    <div className="space-y-3 text-[15px] leading-7 text-slate-200">
      {parts.map((p, idx) => (
        <Fragment key={idx}>{p}</Fragment>
      ))}
    </div>
  );
}

export default Markdown;
